import { getOrigin } from '../config/siteConfig';
import { getGradeColorHexFromThresholds, colorToCss, gradeLegendRows } from './gradePalette';
import { getLithColorHex } from './lithPalette';
import { holePathMine } from './holePath';
import { listCollarRecords } from '../store/holeRecordStore';
import { useHoleDisplayStore } from '../store/holeDisplayStore';
import {
  formatMine,
  resolvedSectionPose,
  sectionAxisLabel,
  sectionNormal,
  wrapAzimuth,
} from './sectionMath';

function len3(v) {
  return Math.hypot(v.e, v.n, v.u) || 1;
}

function norm3(v) {
  const L = len3(v);
  return { e: v.e / L, n: v.n / L, u: v.u / L };
}

function dot3(a, b) {
  return a.e * b.e + a.n * b.n + a.u * b.u;
}

function sub3(a, b) {
  return { e: a.e - b.e, n: a.n - b.n, u: a.u - b.u };
}

function mineVec(p) {
  return { e: p.east, n: p.north, u: p.rl };
}

export function sectionPlotBasis(state, origin = getOrigin()) {
  const pose = resolvedSectionPose(state, origin);
  const center = { east: pose.east, north: pose.north, rl: pose.rl };
  const nScene = sectionNormal({ ...state, lookFlip: false }, origin);
  const n = norm3({ e: nScene.x, n: -nScene.z, u: nScene.y });
  const isPlan = Math.abs(n.u) > 0.92;
  let u;
  let v;
  let xLabel;
  let yLabel;
  if (isPlan) {
    u = { e: 1, n: 0, u: 0 };
    v = { e: 0, n: 1, u: 0 };
    xLabel = 'Easting';
    yLabel = 'Northing';
  } else {
    u = norm3({ e: n.n, n: -n.e, u: 0 });
    v = { e: 0, n: 0, u: 1 };
    if (Math.abs(n.u) > 0.08) {
      v = norm3({
        e: n.n * u.u - n.u * u.n,
        n: n.u * u.e - n.e * u.u,
        u: n.e * u.n - n.n * u.e,
      });
      if (v.u < 0) v = { e: -v.e, n: -v.n, u: -v.u };
    }
    xLabel = `Along ${sectionAxisLabel(state)}`;
    yLabel = Math.abs(v.u) > 0.7 ? 'RL' : 'Up-dip';
  }
  return {
    center,
    n,
    u,
    v,
    isPlan,
    xLabel,
    yLabel,
    width: Math.max(0.1, Number(state.width) || 0.1),
    clipMode: state.clipMode === 'single' ? 'single' : 'corridor',
    pose,
    azimuth: wrapAzimuth(pose.azimuth),
    orientation: state.orientation || pose.orientation,
  };
}

function projectPoint(p, basis) {
  const d = sub3(mineVec(p), mineVec(basis.center));
  return {
    u: dot3(d, basis.u),
    v: dot3(d, basis.v),
    off: dot3(d, basis.n),
    depth: p.depth,
    east: p.east,
    north: p.north,
    rl: p.rl,
  };
}

function clipSegment(a, b, half) {
  const da = a.off;
  const db = b.off;
  const inside = (d) => Math.abs(d) <= half + 1e-6;
  const at = (limit) => {
    const span = db - da;
    if (Math.abs(span) < 1e-9) return null;
    const t = (limit - da) / span;
    if (t < -1e-6 || t > 1 + 1e-6) return null;
    const u = Math.min(1, Math.max(0, t));
    return {
      u: a.u + (b.u - a.u) * u,
      v: a.v + (b.v - a.v) * u,
      off: limit,
      depth: (a.depth ?? 0) + ((b.depth ?? 0) - (a.depth ?? 0)) * u,
      east: a.east + (b.east - a.east) * u,
      north: a.north + (b.north - a.north) * u,
      rl: a.rl + (b.rl - a.rl) * u,
    };
  };
  const pts = [];
  if (inside(da)) pts.push(a);
  if (da !== db) {
    [-half, half].forEach((lim) => {
      if ((da - lim) * (db - lim) < 0) {
        const p = at(lim);
        if (p) pts.push(p);
      }
    });
  }
  if (inside(db)) pts.push(b);
  pts.sort((p, q) => (p.depth ?? 0) - (q.depth ?? 0));
  if (pts.length < 2) return null;
  return [pts[0], pts[pts.length - 1]];
}

function assayColor(grade, thresholds) {
  return colorToCss(getGradeColorHexFromThresholds(grade, thresholds));
}

function lithColor(code) {
  const hex = getLithColorHex(code);
  return `#${Number(hex).toString(16).padStart(6, '0')}`;
}

function intervalColor(record, depthMid, thresholds, mode) {
  if (mode === 'lith') {
    const rows = record?.geology || [];
    for (let i = 0; i < rows.length; i += 1) {
      const from = parseFloat(rows[i].Depth_From);
      const to = parseFloat(rows[i].Depth_To);
      if (depthMid >= from && depthMid <= to) {
        const code = String(rows[i].Lith1_Code || '').trim();
        return code ? lithColor(code) : '#a1a1aa';
      }
    }
    return '#71717a';
  }
  const rows = record?.assays || [];
  for (let i = 0; i < rows.length; i += 1) {
    const from = parseFloat(rows[i].Depth_From);
    const to = parseFloat(rows[i].Depth_To);
    if (depthMid >= from && depthMid <= to) {
      const g = parseFloat(rows[i].Au_ppm);
      return Number.isFinite(g) ? assayColor(g, thresholds) : '#a1a1aa';
    }
  }
  return '#a1a1aa';
}

export function holesOnSection(state, { origin = getOrigin(), desurvey, thresholds, intervalMode } = {}) {
  const basis = sectionPlotBasis(state, origin);
  if (basis.orientation === 'object' || basis.orientation === 'box') {
    return { basis, holes: [], bounds: null };
  }
  const half = basis.clipMode === 'single' ? 0.05 : basis.width / 2;
  const wantDesurvey = desurvey ?? useHoleDisplayStore.getState().desurveyEnabled;
  const mode = intervalMode || 'assay';
  const holes = [];
  listCollarRecords().forEach(({ dataset, holeId, rec }) => {
    const path = holePathMine(rec.collar, rec.surveys, { desurvey: wantDesurvey && rec.hasSurveys });
    if (path.length < 2) return;
    const proj = path.map((p) => projectPoint(p, basis));
    const segs = [];
    for (let i = 0; i < proj.length - 1; i += 1) {
      const clip = clipSegment(proj[i], proj[i + 1], half);
      if (!clip) continue;
      const midDepth = ((clip[0].depth ?? 0) + (clip[1].depth ?? 0)) / 2;
      segs.push({
        a: clip[0],
        b: clip[1],
        color: intervalColor(rec, midDepth, thresholds, mode),
      });
    }
    if (!segs.length) return;
    const collar = projectPoint(path[0], basis);
    holes.push({
      dataset,
      holeId,
      label: holeId,
      collar,
      segs,
      minU: Math.min(...segs.flatMap((s) => [s.a.u, s.b.u])),
      minV: Math.min(...segs.flatMap((s) => [s.a.v, s.b.v])),
      maxU: Math.max(...segs.flatMap((s) => [s.a.u, s.b.u])),
      maxV: Math.max(...segs.flatMap((s) => [s.a.v, s.b.v])),
    });
  });
  holes.sort((a, b) => a.minU - b.minU || a.holeId.localeCompare(b.holeId));
  let bounds = null;
  if (holes.length) {
    bounds = {
      minU: Math.min(...holes.map((h) => h.minU)),
      maxU: Math.max(...holes.map((h) => h.maxU)),
      minV: Math.min(...holes.map((h) => h.minV)),
      maxV: Math.max(...holes.map((h) => h.maxV)),
    };
  }
  return { basis, holes, bounds };
}

export function sectionPlotTitle(state, projectName) {
  const pose = resolvedSectionPose(state);
  const w = Math.max(0.1, Number(state.width) || 0.1);
  const axis = sectionAxisLabel(state);
  return {
    site: projectName || 'Section',
    axis,
    line2: [
      axis,
      `E ${formatMine(pose.east, 1)}`,
      `RL ${formatMine(pose.rl, 1)}`,
      `N ${formatMine(pose.north, 1)}`,
      `${w} m corridor`,
    ].join('  ·  '),
  };
}

export { formatMine, gradeLegendRows };
